import { LegalDoc } from "@/components/marketing/LegalDoc";

const sections = [
  {
    heading: "Agreement to terms",
    body: [
      "These Terms of Service (\"Terms\") are a binding agreement between you and HypeFlow AI, operated by Hypeflow B.V., Herengracht 320, 1016 CE Amsterdam, The Netherlands (\"HypeFlow\", \"we\", \"us\"). They govern your access to and use of our website and the HypeFlow creator marketing platform (the \"Service\").",
      "By creating an account, starting a free account, or otherwise using the Service, you agree to these Terms and to our Privacy Policy. If you are using the Service on behalf of an organization, you represent that you have authority to bind that organization.",
    ],
  },
  {
    heading: "The Service",
    body: [
      "HypeFlow provides tools to discover TikTok and Instagram creators, analyze public performance and audience signals, manage a creator CRM, run campaigns, generate AI-assisted content, and measure results. Features vary by plan and may change as we improve the Service.",
    ],
  },
  {
    heading: "Accounts and eligibility",
    body: [
      "You must provide accurate account information and keep your credentials secure. You are responsible for all activity under your account. You must be at least 16 years old and use the Service for legitimate business purposes. Notify us promptly at support@hypeflow.cc of any unauthorized use.",
    ],
  },
  {
    heading: "Plans, credits, and billing",
    body: [
      {
        list: [
          "Free and paid plans. We offer a Free plan and paid plans (Starter, Growth, Scale) with monthly limits, including a cap on live creator searches. Enterprise plans are offered under separate terms.",
          "Payments. Paid plans are paid up front through Stripe for one month or one year and do not renew automatically. When a period ends without being extended, the account returns to the Free plan.",
          "Studio credits. Paid plans include monthly AI Creative Studio credits, which roll over while the account exists. Additional credit packs are one-time purchases. Credits have no cash value and are non-refundable except where required by law.",
          "Usage limits. Live search quotas and other limits apply per plan and reset monthly. Exceeding a limit may require upgrading your plan.",
          "Changes and cancellation. You may upgrade, downgrade, or cancel at any time; changes take effect per the billing cycle. Fees already paid are non-refundable except where required by law.",
          "Taxes. Fees are exclusive of applicable taxes, which you are responsible for.",
        ],
      },
    ],
  },
  {
    heading: "Acceptable use",
    body: [
      "You agree not to:",
      {
        list: [
          "Use the Service to violate any law or the terms of TikTok, Instagram, or other third-party platforms.",
          "Send spam, harassment, deceptive, or unsolicited bulk outreach.",
          "Attempt to scrape, overload, reverse engineer, or gain unauthorized access to the Service or its data providers.",
          "Resell, sublicense, or expose the Service or its data to unauthorized third parties.",
          "Upload malicious code or infringe the intellectual property or privacy rights of others.",
        ],
      },
      "We may suspend or terminate accounts that violate these Terms or that create risk or legal exposure for us or others.",
    ],
  },
  {
    heading: "Creator data and third-party platforms",
    body: [
      "The Service surfaces publicly available information about creators obtained through third-party data providers, together with modeled estimates such as audience demographics and authenticity signals. These metrics and estimates are provided \"as is\" for research purposes, may be incomplete or inaccurate, and are not guarantees of performance, audience composition, or authenticity.",
      "You are responsible for your own compliance with the policies of TikTok, Instagram, and any other platform, and for how you contact and contract with creators. HypeFlow is not affiliated with, endorsed by, or sponsored by TikTok or Instagram.",
    ],
  },
  {
    heading: "AI-assisted features",
    body: [
      "Some features use AI to generate content such as briefs, outreach drafts, and analyses based on inputs you provide. AI output may be inaccurate or unsuitable and should be reviewed by a human before use. You are responsible for content you generate and send.",
    ],
  },
  {
    heading: "Your content and data",
    body: [
      "You retain ownership of the data and content you add to the Service (\"Customer Data\"). You grant us a limited license to host and process Customer Data solely to provide and improve the Service. Our handling of personal information is described in our Privacy Policy.",
    ],
  },
  {
    heading: "Intellectual property",
    body: [
      "The Service, including its software, design, branding, and content (excluding Customer Data), is owned by HypeFlow and protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable right to use the Service during your subscription. You may not copy, modify, or create derivative works except as permitted by these Terms.",
    ],
  },
  {
    heading: "Third-party services",
    body: [
      "The Service integrates third-party providers (including Stripe, Resend, social data providers, and AI model providers). Your use of those features may be subject to the third party's terms. We are not responsible for third-party services, and their availability may affect parts of the Service.",
    ],
  },
  {
    heading: "Disclaimers",
    body: [
      "The Service is provided \"as is\" and \"as available\" without warranties of any kind, whether express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or that any data, estimate, or AI output will be accurate or achieve any particular result.",
    ],
  },
  {
    heading: "Limitation of liability",
    body: [
      "To the maximum extent permitted by law, HypeFlow and its affiliates will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or goodwill. Our total liability for any claim relating to the Service will not exceed the amounts you paid us in the 12 months before the event giving rise to the claim.",
      "Nothing in these Terms limits liability that cannot be limited under applicable law, including mandatory consumer protection rules under Dutch and EU law where they apply.",
    ],
  },
  {
    heading: "Indemnification",
    body: [
      "You agree to indemnify and hold HypeFlow harmless from claims, damages, and expenses arising from your use of the Service, your Customer Data, or your breach of these Terms or applicable law.",
    ],
  },
  {
    heading: "Termination",
    body: [
      "You may stop using the Service and close your account at any time. We may suspend or terminate access if you breach these Terms, fail to pay, or create legal or security risk. Upon termination, your right to use the Service ends; sections that by their nature should survive (such as IP, disclaimers, and liability) will survive.",
    ],
  },
  {
    heading: "Governing law",
    body: [
      "These Terms are governed by the laws of the Netherlands, without regard to conflict-of-law rules. The competent courts of Amsterdam, the Netherlands, have jurisdiction over any disputes, subject to any mandatory rights you have under the consumer law of your country of residence.",
    ],
  },
  {
    heading: "Changes to these terms",
    body: [
      "We may update these Terms from time to time. When changes are material, we will update the \"Last updated\" date and, where appropriate, notify you. Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.",
    ],
  },
  {
    heading: "Contact us",
    body: [
      "Questions about these Terms? Email us at support@hypeflow.cc.",
      "Hypeflow B.V., Herengracht 320, 1016 CE Amsterdam, The Netherlands.",
    ],
  },
];

export default function Terms() {
  return (
    <LegalDoc
      testid="terms-page"
      eyebrow="Legal"
      title="Terms of Service"
      updated="8 June 2026"
      intro="These terms explain the rules for using HypeFlow AI — your plan, your responsibilities, and ours. Please read them carefully."
      sections={sections}
    />
  );
}
